import { Badge, Card, CardContent } from '@/ui/primitives';
import { MapPin } from 'lucide-react';
import { BandLadder } from '@/components/BandLadder';
import { formatStationAddress } from '@/domain/address';
import type { BandId } from '@/domain/bands';
import { formatDistanceKm } from '@/domain/geo';
import type { Dictionary, Locale } from '@/i18n';
import type { LookupResult } from '@/hooks/useLookup';

type ResultPanelProps = {
  dict: Dictionary;
  locale: Locale;
  result: LookupResult;
};

type BadgeVariant = 'green' | 'blue' | 'yellow' | 'red' | 'grey';

const BADGE = {
  excellent: 'green',
  covered: 'blue',
  limited: 'yellow',
  far: 'red',
  out_of_region: 'grey',
} satisfies Record<BandId, BadgeVariant>;

export const ResultPanel = ({ dict, locale, result }: ResultPanelProps) => {
  const { band, nearest } = result;
  const distance = formatDistanceKm(nearest.distanceKm, locale);

  return (
    <Card role="status" aria-live="polite" className="animate-result-in border-grey-200">
      <CardContent className="flex flex-col gap-5 p-5">
        {/* Out of region sits off the scale entirely, so four grey segments would be
            noise on the one message that has to land cleanly. */}
        {band === 'out_of_region' ? null : (
          <BandLadder dict={dict} band={band} />
        )}

        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-extrabold leading-tight tracking-tight text-black">
            {dict.bands[band].title}
          </h2>
          <p className="text-sm leading-relaxed text-grey-800">{dict.bands[band].body}</p>
        </div>

        {band === 'out_of_region' ? null : (
          <div className="flex flex-col gap-2 border-t border-grey-200 pt-4">
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-grey-800">
              {dict.result.nearestStation}
            </span>

            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-grey-800" aria-hidden="true" />
              <span className="text-sm font-medium text-black">
                {formatStationAddress(nearest.address)}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={BADGE[band]} className="tabular-nums">
                {distance}
              </Badge>
              <Badge variant="grey">
                {dict.result.stationsWithin(result.withinCount, result.withinRadiusKm)}
              </Badge>
            </div>

            <p className="text-[11px] text-grey-800">{dict.result.distanceNote}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
